/**
 * KTS WebSocket Client — Connects to the Kinetic Telemetry Service
 * and streams bitblocks for rep counting.
 */

export interface BitblockPayload {
  event: 'rep_completed' | 'rep_started' | 'session_start' | 'session_end' | 'heartbeat';
  timestamp_ms: number;
  confidence_score: number;
  device_telemetry: {
    accelerometer?: { x: number; y: number; z: number };
    gyroscope?: { x: number; y: number; z: number };
  };
  joint_data?: Record<string, unknown>;
}

export interface BitblockAck {
  ack: boolean;
  sequence: number;
  verified_reps: number;
  hash: string;
}

export interface KtsConnectedMessage {
  type: 'connected';
  session_id: string;
  message: string;
}

export type KtsMessage = BitblockAck | KtsConnectedMessage | { error: string };

type MessageHandler = (msg: KtsMessage) => void;

export class KtsClient {
  private ws: WebSocket | null = null;
  private handlers: Set<MessageHandler> = new Set();
  private _connected = false;

  get connected(): boolean {
    return this._connected;
  }

  /**
   * Connect to the KTS WebSocket server.
   * @param wsUrl Full WebSocket URL including session_id query param
   * @param token JWT auth token (appended as query param)
   */
  connect(wsUrl: string, token: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // Append token to URL
      const url = new URL(wsUrl);
      url.searchParams.set('token', token);

      this.ws = new WebSocket(url.toString());

      this.ws.onopen = () => {
        this._connected = true;
        resolve();
      };

      this.ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data as string);
          for (const handler of this.handlers) {
            handler(msg);
          }
        } catch {
          console.warn('[kts-client] Failed to parse message', event.data);
        }
      };

      this.ws.onerror = (err) => {
        console.error('[kts-client] WebSocket error', err);
        this._connected = false;
        reject(new Error('WebSocket connection failed'));
      };

      this.ws.onclose = () => {
        this._connected = false;
      };
    });
  }

  /** Register a handler for incoming messages */
  onMessage(handler: MessageHandler): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  /** Send a bitblock to the KTS server */
  sendBitblock(payload: BitblockPayload): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('[kts-client] Cannot send — not connected');
      return;
    }
    this.ws.send(JSON.stringify(payload));
  }

  /** Send a rep_completed bitblock (convenience) */
  sendRep(confidence: number = 1.0): void {
    this.sendBitblock({
      event: 'rep_completed',
      timestamp_ms: Date.now(),
      confidence_score: confidence,
      device_telemetry: {},
    });
  }

  /** Close the WebSocket connection */
  close(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
      this._connected = false;
    }
    this.handlers.clear();
  }
}
